import { X, Sparkles, Target, Lightbulb, Check, XCircle, ArrowRight, PenLine } from "lucide-react";

const PIE_IMG =
  "https://images.pexels.com/photos/12782728/pexels-photo-12782728.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SWEET_SPOT =
  "Show me how to make a homemade apple pie with a lattice crust and cinnamon.";

const RULES = [
  {
    Icon: Target,
    title: "Name a detail or two that show up on screen",
    body: "A key ingredient, the style, or the one thing that must be right (a lattice crust, a touch of cinnamon). Those shape your images and voice-over — Vittyyo fills in the rest.",
  },
  {
    Icon: PenLine,
    title: "Let the toggles do the rest",
    body: "Skill level controls how expert the tone sounds — you don't need to type “explain like a pro”, just pick Pro. Every video is a full, richly detailed walkthrough.",
  },
  {
    Icon: Lightbulb,
    title: "A short sentence is the sweet spot",
    body: "Keep it simple — a basic request with a detail or two. Vittyyo expands it into a rich, detailed prompt for you.",
  },
];

export default function GoalGuideModal({ open, onClose, onUseExample }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[85] flex items-start justify-center p-4 py-8 overflow-y-auto"
      style={{ background: "rgba(23,20,38,.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="goal-guide-modal"
    >
      <div
        className="sms-tweed bg-[#0f0f16] rounded-[26px] w-full max-w-2xl relative border-2 border-[#5d8a52] overflow-hidden"
        style={{ boxShadow: "0 30px 90px -20px rgba(0,0,0,.75), 0 0 0 1px rgba(93,138,82,.25), 0 0 40px -6px rgba(93,138,82,.35)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title header bar */}
        <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-4 bg-black border-b-2 border-[#5d8a52] text-white">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-[#4f7746] flex items-center justify-center shrink-0" style={{ boxShadow: "0 0 18px -2px rgba(143,191,130,.6)" }}>
              <Sparkles size={19} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#8fbf82] leading-none">Quick guide</div>
              <h2 className="text-lg md:text-xl font-extrabold leading-tight truncate text-white">How to describe your goal</h2>
            </div>
          </div>
          <button
            data-testid="goal-guide-close"
            onClick={onClose}
            className="p-2 rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-colors shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 md:p-8">
          {/* Hero */}
          <div className="flex items-center justify-center gap-2 text-[#8fbf82] font-bold text-sm">
            <Sparkles size={16} /> GLAD YOU ASKED
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold mt-2 leading-tight text-white text-center">
            What should I say to get the best result?
          </h2>
          <p className="text-white/70 mt-3 text-base md:text-lg leading-relaxed text-center">
            Just give Vittyyo a <span className="font-semibold text-[#8fbf82]">basic request</span> — a sentence with
            a detail or two. You don't need a long, detailed description; Vittyyo expands your idea into a rich prompt
            and turns it into a photo-illustrated manual <em>and</em> a narrated video. It takes about ten seconds.
          </p>

          {/* The apple pie example */}
          <div className="mt-6 rounded-2xl bg-black border-2 border-[#5d8a52] overflow-hidden" style={{ boxShadow: "0 0 30px -10px rgba(93,138,82,.4)" }}>
            <div className="flex flex-col sm:flex-row">
              <img
                src={PIE_IMG}
                alt="Freshly baked apple pie with a lattice crust"
                loading="eager"
                className="w-full sm:w-[190px] h-44 sm:h-auto object-cover shrink-0 bg-[#0e0e13]"
                data-testid="goal-guide-pie-img"
              />
              <div className="p-5 flex-1">
                <div className="text-sm font-bold text-[#8fbf82] mb-3">Let's use an apple pie as an example 🥧</div>

                <div className="flex items-start gap-2 mb-3">
                  <XCircle size={18} className="text-[#f0736f] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-bold text-[#f0736f] uppercase tracking-wide">Too vague</div>
                    <div className="text-sm text-white/60 italic">“Show me how to bake an apple pie.”</div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Check size={18} className="text-[#6fdfa0] shrink-0 mt-0.5" strokeWidth={3} />
                  <div>
                    <div className="text-xs font-bold text-[#6fdfa0] uppercase tracking-wide">Just right</div>
                    <div className="text-sm text-white font-medium">“{SWEET_SPOT}”</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-sm text-white/70 mt-4 leading-relaxed text-center">
            See the difference? The good one names just a detail or two that actually <span className="font-semibold text-[#8fbf82]">appear
            in your video</span> — a lattice crust, a hint of cinnamon — no essay needed. Vittyyo expands your idea into
            a rich, detailed prompt, and the tone is handled by the{" "}
            <span className="font-semibold text-[#8fbf82]">Skill level</span> you pick below. Every video is a full, detailed walkthrough.
          </p>

          {/* Rules */}
          <div className="mt-5 space-y-3">
            {RULES.map(({ Icon, title, body }, i) => (
              <div key={i} className="flex items-start gap-3 rounded-2xl bg-black border-2 border-[#5d8a52] p-3.5 transition-transform hover:scale-[1.01]">
                <div className="w-10 h-10 rounded-xl bg-[#4f7746] text-white flex items-center justify-center shrink-0" style={{ boxShadow: "0 0 14px -2px rgba(143,191,130,.5)" }}>
                  <Icon size={19} />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-[15px] leading-tight text-white">{title}</div>
                  <div className="text-sm text-white/65 mt-1 leading-snug">{body}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-2xl bg-black border-2 border-[#5d8a52] text-white p-4">
            <div className="font-bold text-sm flex items-center gap-2 mb-1.5">
              <Lightbulb size={15} className="text-[#f0c975]" /> The 10-second recipe for a great result
            </div>
            <div className="text-sm text-white/70 leading-relaxed">
              <span className="font-semibold text-white">1–2 vivid sentences</span> in the box → pick your{" "}
              <span className="font-semibold text-white">Skill level</span> (Pro for expert tone) → press create. That's it.
            </div>
          </div>

          {/* CTAs */}
          <div className="grid sm:grid-cols-2 gap-3 mt-6 items-stretch">
            <button
              data-testid="goal-guide-use-example"
              onClick={() => { onUseExample?.(SWEET_SPOT); onClose(); }}
              className="flex items-center justify-center gap-2 bg-[#4f7746] hover:bg-[#3f6238] text-white font-bold py-3 px-4 rounded-2xl transition-transform hover:scale-[1.02]"
            >
              <Sparkles size={18} /> Try this pie idea
            </button>
            <button
              data-testid="goal-guide-start"
              onClick={onClose}
              className="flex items-center justify-center gap-2 bg-transparent hover:bg-[#5d8a52]/15 text-[#8fbf82] font-bold py-3 px-4 rounded-2xl transition-colors border-2 border-[#5d8a52]"
            >
              Got it — I'll write my own <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
